import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Logo from '../Logo';
import { COLORS } from './landingData';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { MobileMenuSections } from '../MobileMenuSections';

const LandingNavbar = ({ mobileMenuOpen, setMobileMenuOpen, navigate }) => {
  return (
    <nav style={{ 
      background: COLORS.white,
      borderBottom: `1px solid ${COLORS.sage}20`,
      padding: '0.75rem 0',
      position: 'sticky',
      top: 0,
      zIndex: 50
    }}>
      <div className="container mx-auto px-4 flex items-center justify-between gap-2">
        <div style={{ flex: '0 0 auto' }} className="header-logo max-w-[140px] sm:max-w-[180px] md:max-w-none">
          <Logo />
        </div>
        
        {/* Menu Toggle - ALL SCREEN SIZES */}
        <div className="flex items-center gap-3" style={{ flexShrink: 0 }}>
          <a
            href="/help"
            data-testid="header-help-tonight-link"
            style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, minHeight: 36, color: '#B91C1C', fontWeight: 700, fontSize: '0.75rem', lineHeight: 1, textDecoration: 'none', border: '1.5px solid #B91C1C', borderRadius: '9999px', padding: '0.45rem 0.7rem', whiteSpace: 'nowrap' }}
          >
            Help now
          </a>
          <a 
            href="/login"
            data-testid="header-login-link" 
            style={{ 
              color: COLORS.navy, 
              fontWeight: 500,
              fontSize: '0.875rem',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            Login
          </a>
          {/* Language switcher hidden until more pages are translated to Spanish */}
          {false && <LanguageSwitcher style={{ color: COLORS.navy }} />}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, minWidth: 44, minHeight: 44, flex: '0 0 44px', boxSizing: 'border-box' }}
            data-testid="hamburger-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X size={26} color={COLORS.navy} strokeWidth={2.5} style={{ width: 26, height: 26, minWidth: 26, minHeight: 26, flexShrink: 0, display: 'block' }} />
            ) : (
              <Menu size={26} color={COLORS.navy} strokeWidth={2.5} style={{ width: 26, height: 26, minWidth: 26, minHeight: 26, flexShrink: 0, display: 'block' }} />
            )}
          </button>
        </div>
      </div>

      {/* Menu Dropdown - ALL SCREEN SIZES */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              overflow: 'hidden',
              background: COLORS.white,
              borderTop: `1px solid ${COLORS.sage}20`,
              maxHeight: 'calc(100vh - 64px)',
              overflowY: 'auto'
            }}
          >
            <div style={{ 
              padding: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              <motion.a
                onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/quiz'); }}
                href="/quiz"
                data-testid="menu-quiz-link"
                whileTap={{ scale: 0.98 }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.9rem 1rem', background: '#5f7057', borderRadius: '0.75rem', fontSize: '0.9375rem', fontWeight: 700, color: '#fff', textDecoration: 'none', cursor: 'pointer', boxShadow: '0 6px 18px rgba(95,112,87,0.35)' }}
              >
                <span>Which course is for me? <span style={{ fontWeight: 500, opacity: 0.9 }}>· 90-second quiz</span></span>
                <span style={{ background: '#fff', color: '#5f7057', fontSize: '0.7rem', fontWeight: 800, borderRadius: 999, padding: '0.15rem 0.55rem', letterSpacing: '1px', whiteSpace: 'nowrap', flexShrink: 0 }}>START HERE</span>
              </motion.a>
              <motion.a
                onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/help'); }}
                href="/help"
                data-testid="menu-help-tonight-link"
                whileTap={{ scale: 0.98 }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#FEF2F2', borderRadius: '0.75rem', fontSize: '0.9375rem', fontWeight: 700, color: '#B91C1C', textDecoration: 'none', cursor: 'pointer', border: '2px solid #B91C1C' }}
              >
                <span>🆘</span>
                <span>In crisis right now? Help for tonight</span>
              </motion.a>
              <motion.a
                onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/recovery-to-service'); }}
                href="/recovery-to-service"
                data-testid="menu-recovery-to-service-link"
                whileTap={{ scale: 0.98 }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.9rem 1rem', background: '#2A3B4A', borderRadius: '0.75rem', fontSize: '0.9375rem', fontWeight: 700, color: '#fff', textDecoration: 'none', cursor: 'pointer', boxShadow: '0 6px 18px rgba(42,59,74,0.35)' }}
              >
                <span>Recovery to Service Qualification</span>
                <span style={{ background: '#C4703C', color: '#fff', fontSize: '0.7rem', fontWeight: 800, borderRadius: 999, padding: '0.15rem 0.55rem', letterSpacing: '1px', whiteSpace: 'nowrap', flexShrink: 0 }}>NEW</span>
              </motion.a>
              <motion.a
                onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/what-we-offer'); }}
                href="/courses"
                data-testid="menu-what-we-offer-row"
                whileTap={{ scale: 0.98 }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.85rem 1rem', background: '#F5F1E8', border: '1px solid #8A9A8655', borderRadius: '0.75rem', fontSize: '0.9375rem', fontWeight: 700, color: '#1B2A41', textDecoration: 'none', cursor: 'pointer' }}
              >
                <span>What we offer <span style={{ fontWeight: 500, opacity: 0.75 }}>· individuals · families · churches · R2S</span></span>
                <span style={{ color: '#C25E4B', fontWeight: 800 }}>→</span>
              </motion.a>
              <MobileMenuSections
                navigate={navigate}
                closeMenu={() => setMobileMenuOpen(false)}
                onRealStoriesClick={() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' })}
              />
              <motion.button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/signup');
                }}
                whileTap={{ scale: 0.98 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  background: COLORS.navy,
                  color: COLORS.white,
                  borderRadius: '0.75rem',
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <span>🚀</span>
                <span>Start Free</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default LandingNavbar;
